import React, { useEffect, useState } from "react";
import { Order } from "../../../interfaces/order";
import { getOrderByID } from "../../../services/orderService";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderForm } from "../../../interfaces/orderForm";
import {
  getOrderFormByID,
  saveReferenceScreeningAuthForm,
} from "../../../services/orderFormService";
import AuthorizationForm, {
  Authorization,
} from "../../../components/form-components/AuthorizationForm";
import { getSimpleClient } from "../../../services/clientService";
import { SimpleClient } from "../../../interfaces/client";
import { getReferenceOrderByOrderID } from "../../../services/referenceOrderService";
import { ReferenceOrder } from "../../../interfaces/referenceOrder";
import { formatDate, isValidDate } from "../../../services/helpers";
import { getAdditionalForm } from "../../../services/additionalFormService";
import { HttpService } from "../../../services/httpService";
import { ReferenceAuthorization } from "../../../interfaces/referenceAuthorization";
import { ReferenceAuthorizationInstruction } from "../../../interfaces/referenceAuthorizationInstruction";
import {
  getReferenceAuthorizationBase,
  getReferenceAuthorizationInstructions,
} from "../../../services/referenceAuthorizationService";

const ReferenceScreeningAuthForm = () => {
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [client, setClient] = useState<SimpleClient | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [referenceOrder, setReferenceOrder] = useState<ReferenceOrder | null>(
    null,
  );
  const [orderForm, setOrderForm] = useState<OrderForm | null>(null);
  const location = useLocation();
  const { orderID, orderFormID } = location.state || {};
  const [authorization, setAuthorization] = useState<Authorization>({
    checked: false,
    dated: new Date(),
    aCity: "",
    aState: "",
    fullName: "",
  });
  const [referenceAuthorization, setReferenceAuthorization] =
    useState<ReferenceAuthorization>();
  const [instructions, setInstructions] =
    useState<ReferenceAuthorizationInstruction>();
  const [pageDecorationUrl, setPageDecorationUrl] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const order = await getOrderByID(orderID);
        setOrder(order);
        const referenceOrder = await getReferenceOrderByOrderID(orderID);
        setReferenceOrder(referenceOrder);
        const orderForm = await getOrderFormByID(orderFormID);
        setOrderForm(orderForm);
        const simpleClient = await getSimpleClient(order?.clientID);
        setClient(simpleClient);

        const refAuthResponse = await getReferenceAuthorizationBase(
          simpleClient.clientID,
        );
        setReferenceAuthorization(refAuthResponse);

        const refAuthInstructionsResponse =
          await getReferenceAuthorizationInstructions(
            simpleClient.clientID,
            referenceOrder?.years,
          );
        setInstructions(refAuthInstructionsResponse);

        const pageDecorationForm = await getAdditionalForm(
          simpleClient.clientID,
          5,
        );

        if (pageDecorationForm) {
          const pageDecorationUrl = await HttpService.getFile(
            "page-decorations",
            simpleClient.clientID.toString(),
            pageDecorationForm.completedFormUrl,
          );
          setPageDecorationUrl(pageDecorationUrl);
        }
      } catch (error) {
        console.error("Error fetching candidate, order or orderForm:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderFormID, orderID]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
  ) => {
    let value: string | boolean | Date =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (field in authorization) {
      setAuthorization((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    }
    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    // Authorization Validation
    if (!authorization.checked) {
      errors.checked = "You must agree before continuing.";
    }
    if (!isValidDate(authorization.dated)) {
      errors.dated = "Date is required and must be valid.";
    }
    if (!authorization.fullName) {
      errors.fullName = "Full name is required.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    setLoadingSpinner(true);

    try {
      if (
        (await validateFields()) &&
        order &&
        orderForm &&
        client &&
        referenceOrder
      ) {
        const logoForm = await getAdditionalForm(client.clientID, 5);
        const result = await saveReferenceScreeningAuthForm(
          order,
          orderForm?.orderFormID,
          authorization,
          client.diocese ? client.diocese : "",
          client.diocese2 ? client.diocese2 : "",
          client.clientName ? client.clientName : "",
          referenceOrder,
          logoForm,
          client.clientID,
        );

        if (result) {
          navigate("/candidate/needs-review", {
            state: { successMessage: "Form successfully submitted." },
          });
        }
      }
    } catch (error) {
      console.error("Error saving reference screening authorization:", error);
      alert("An error occurred while saving the form. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      <div className="form-header-container">
        <h1>
          {order?.candidateFirstName} {order?.candidateLastName}
        </h1>
        <div className="page-items-container"></div>
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>

          <div className="data-container">
            <div
              className="questionnaire-header page-deco-container"
              style={{ width: "100%" }}
            >
              {pageDecorationUrl && (
                <div className="page-decoration">
                  <img
                    src={pageDecorationUrl}
                    alt="Logo"
                    style={{
                      maxWidth: "100px",
                      maxHeight: "100px",
                      height: "auto",
                    }}
                  />
                </div>
              )}
              <div className="text-form-header">REFERENCE AUTHORIZATION</div>
              {client?.diocese2?.trim() && (
                <div style={{ fontWeight: "bold", textAlign: "center" }}>
                  {client.diocese2.toUpperCase()}
                </div>
              )}
              {client?.diocese?.trim() && (
                <div style={{ fontWeight: "bold", textAlign: "center" }}>
                  {client.diocese.toUpperCase()}
                </div>
              )}
            </div>
            {/* DYNAMIC */}
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              {referenceAuthorization?.baseTextOne}
            </div>

            {/* DYNAMIC */}
            {referenceOrder?.referenceTypeID !== 2 && (
              <div className="text-body" style={{ flex: "0 0 100%" }}>
                {referenceAuthorization?.baseTextTwo}
              </div>
            )}

            {/* Update */}
            {referenceOrder?.referenceTypeID === 2 && (
              <>
                <div className="text-body">
                  <b>
                    Our records indicate that you last completed a background
                    screening on {formatDate(referenceOrder?.priorDate)} in the
                    Diocese of {client?.diocese}.
                  </b>
                </div>
                <div className="text-body">
                  The update process requires you to again complete a screening
                  questionnaire and answer a series of questions which are
                  intimate in nature. You must answer all questions. When
                  completed, please return the screening questionnaire directly
                  to Oxford Document Management in the envelope provided. Except
                  as may be required by law, the bishop or the bishop's
                  designated agent(s) will be the only persons to see the
                  information you supply (when done in conjunction with a
                  deployment process, information may also be shared with your
                  current bishop). The information you provide will be kept as
                  part of our confidential files, but where mandated by law (for
                  example, suspected abuse of a minor or an incompetent person
                  or lawful subpoena), the information will be directed to legal
                  authorities.
                </div>
              </>
            )}

            {/* DYNAMIC */}
            {referenceOrder?.referenceTypeID !== 2 && (
              <div className="text-body" style={{ flex: "0 0 100%" }}>
                {referenceAuthorization?.baseTextThree}
              </div>
            )}

            {/* INSTRUCTIONS */}
            <div>
              <ol className="alphabet-list">
                <li>{instructions?.instructionOne}</li>
                <li>{instructions?.instructionTwo}</li>
                <li>{instructions?.instructionThree}</li>
              </ol>
            </div>

            {/* Update */}
            {referenceOrder?.referenceTypeID === 2 && (
              <div className="text-body" style={{ flex: "0 0 100%" }}>
                To enable us to update your prior background screening, please
                supply the names, addresses and, if available, telephone numbers
                of the following on the enclosed "Identification Form"
                <ul>
                  <li>
                    All educational institutions you attended for at least on
                    semester/quarter or more{" "}
                    <b>since {formatDate(referenceOrder.priorDate)}</b>.
                  </li>
                  <li>
                    All employers and/or parishes served{" "}
                    <b>since {formatDate(referenceOrder.priorDate)}</b>.
                  </li>
                  <li>
                    All bishops and dioceses having canonical authority over you{" "}
                    <b>since {formatDate(referenceOrder.priorDate)}</b>.
                  </li>
                </ul>
              </div>
            )}
          </div>

          <AuthorizationForm
            authText={referenceAuthorization?.authorization}
            authorization={authorization}
            validationErrors={validationErrors}
            onInputChange={handleInputChange}
            type={""}
          />

          <div className="btn-center">
            <button className="oxbtn btn-green" onClick={() => handleSave()}>
              Submit
            </button>
          </div>
          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};
export default ReferenceScreeningAuthForm;
